import { create } from 'zustand'
import { combine, devtools } from 'zustand/middleware'

export type Theme = 'light' | 'dark' | 'system'

// Source of truth for the color scheme. Deliberately not coupled to Mantine:
// Mantine is being removed, so theme state lives here and only `useDarkMode`
// applies it to Tailwind's `.dark` selector.
//
// Persisted to a plain localStorage string (not the zustand-persist envelope)
// so the inline FOUC script in apps/portal/index.html can read it directly
// before React loads. Keep this key in sync with that script.
const THEME_STORAGE_KEY = 'seen-theme'

// Defaults to 'light' (not 'system') for now: out of the box the whole app
// stays light and nothing changes for users who never touch the toggle. The
// default will move to 'system' later.
function readStoredTheme(): Theme {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(THEME_STORAGE_KEY)
  } catch {}
  return stored === 'light' || stored === 'dark' || stored === 'system'
    ? stored
    : 'light'
}

export const useThemeStore = create(
  devtools(
    combine({ theme: readStoredTheme() }, (set) => ({
      setTheme: (theme: Theme) => {
        // localStorage can throw (quota, storage disabled by policy); the theme
        // change should still apply even if it can't be persisted.
        try {
          localStorage.setItem(THEME_STORAGE_KEY, theme)
        } catch {}
        set({ theme })
      },
    }))
  )
)
